import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GodkjennOgBookPanel from "../components/GodkjennOgBookPanel";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const KAT_LABELS = { sykkelservice: "Sykkel", skiservice: "Ski" };

const fmtDT = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffMin = Math.floor((now - dt) / 60000);
  if (diffMin < 1) return "Nå nettopp";
  if (diffMin < 60) return `${diffMin} min siden`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} t siden`;
  return dt.toLocaleString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtKr = (n) => (typeof n === "number" ? `${n.toLocaleString("nb-NO")} kr` : "");

export default function AkuttDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [akutt, setAkutt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [markingUnread, setMarkingUnread] = useState(false);
  const [closing, setClosing] = useState(false);
  const [avvising, setAvvising] = useState(false);
  const threadRef = useRef(null);

  async function fetchAkutt() {
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/${id}`, { cache: "no-store" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setAkutt(data);
    } catch {
      setError("Kunne ikke hente forespørselen.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAkutt();
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (!akutt) return;
    const hasUnread = (akutt.messages || []).some((m) => m.from === "customer" && !m.readAt);
    if (hasUnread) {
      fetch(`${API_BASE_URL}/akutt/${id}/les`, { method: "PATCH" }).catch(() => {});
    }
  }, [akutt, id]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [akutt?.messages?.length]);

  const handleSend = async () => {
    const tekst = replyText.trim();
    if (!tekst) return;
    setSending(true);
    setSendError("");
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/${id}/svar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tekst }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Feil");
      }
      setReplyText("");
      await fetchAkutt();
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleMarkUnread = async () => {
    setMarkingUnread(true);
    try {
      await fetch(`${API_BASE_URL}/akutt/${id}/mark-unread`, { method: "PATCH" });
      navigate("/varsling");
    } catch {
      setMarkingUnread(false);
    }
  };

  const handleSteng = async () => {
    if (!window.confirm("Stenge forespørselen? Kunden vil ikke kunne sende flere meldinger i denne tråden.")) return;
    setClosing(true);
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/${id}/steng`, { method: "PATCH" });
      const data = await r.json();
      setAkutt(data);
    } catch { /* silent */ } finally {
      setClosing(false);
    }
  };

  const handleAvvis = async () => {
    if (!window.confirm("Avvise forespørselen uten å booke service?")) return;
    setAvvising(true);
    try {
      const r = await fetch(`${API_BASE_URL}/akutt/${id}/avvis`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await r.json();
      setAkutt(data);
    } catch { /* silent */ } finally {
      setAvvising(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Laster forespørsel…</div>;
  if (error) return <p className="text-red-500 text-center mt-8">{error}</p>;
  if (!akutt) return null;

  const bikeInfo = [akutt.merke, akutt.modell].filter(Boolean).join(" ");
  const messages = akutt.messages || [];
  const erFerdigbehandlet = akutt.godkjent || akutt.avvist;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-blue-600 hover:underline mb-2 block"
          >
            ← Tilbake
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-800">
              Akutt
            </span>
            <h1 className="text-xl font-bold text-gray-900">{akutt.navn}</h1>
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
            <a href={`tel:${akutt.telefon}`} className="text-blue-600 hover:underline font-mono">
              {akutt.telefon}
            </a>
            {bikeInfo && (
              <span className="bg-gray-100 rounded px-2 py-0.5 text-xs">{bikeInfo}</span>
            )}
            {akutt.godkjent ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Godkjent og booket
              </span>
            ) : akutt.avvist ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                Avvist
              </span>
            ) : akutt.erStengt ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                Stengt
              </span>
            ) : (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                akutt.besvart ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
              }`}>
                {akutt.besvart ? "Besvart" : "Venter på svar"}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!erFerdigbehandlet && !akutt.erStengt && (
            <button
              onClick={handleSteng}
              disabled={closing}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {closing ? "…" : "Steng"}
            </button>
          )}
          {!akutt.erStengt && (
            <button
              onClick={handleMarkUnread}
              disabled={markingUnread}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              {markingUnread ? "…" : "Merk som ulest"}
            </button>
          )}
        </div>
      </div>

      {/* Valgte tjenester + kommentar */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        {akutt.onsketButikkNavn && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Ønsket verksted</p>
            <p className="text-sm text-gray-800">{akutt.onsketButikkNavn}</p>
          </div>
        )}
        {(akutt.kategori || akutt.tjenesteType) && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Kategori / tjenestetype</p>
            <p className="text-sm text-gray-800">
              {[KAT_LABELS[akutt.kategori] || akutt.kategori, akutt.tjenesteType].filter(Boolean).join(" · ")}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Valgte tjenester</p>
          {(akutt.tjenester || []).length === 0 ? (
            <p className="text-sm text-gray-400">Ingen tjenester valgt</p>
          ) : (
            <ul className="text-sm text-gray-800 space-y-1">
              {akutt.tjenester.map((t, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span>{t.name}</span>
                  {typeof t.price === "number" && <span className="text-gray-500">{fmtKr(t.price)}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Kommentar fra kunde</p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{akutt.kommentar}</p>
        </div>
      </div>

      {/* Samtaletråd */}
      <div
        ref={threadRef}
        className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 max-h-[45vh] overflow-y-auto"
      >
        {messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Ingen meldinger</p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.from === "customer" ? "justify-start" : "justify-end"}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  m.from === "customer"
                    ? "bg-white border border-gray-200 text-gray-800"
                    : "bg-red-600 text-white"
                }`}
              >
                <p className="leading-snug">{m.tekst}</p>
                <p className={`text-[10px] mt-1 ${m.from === "customer" ? "text-gray-400" : "text-red-200"}`}>
                  {m.from === "customer" ? akutt.navn : (m.av || "Ansatt")} · {fmtDT(m.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Svarfelt eller stengt-melding */}
      {akutt.erStengt || erFerdigbehandlet ? (
        akutt.godkjent ? null : (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-5 text-center space-y-1">
            <p className="text-sm font-medium text-gray-700">
              {akutt.avvist ? "Forespørselen er avvist" : "Denne forespørselen er stengt"}
            </p>
          </div>
        )
      ) : (
        <div className="space-y-2">
          <textarea
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            placeholder="Skriv svar — sendes som SMS med lenke til oppfølgingsportalen"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend();
            }}
          />
          {sendError && <p className="text-xs text-red-600">{sendError}</p>}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Ctrl+Enter for å sende</span>
            <button
              onClick={handleSend}
              disabled={!replyText.trim() || sending}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
            >
              {sending ? "Sender…" : "Send svar via SMS"}
            </button>
          </div>
        </div>
      )}

      {/* Godkjenn og book tid */}
      {!erFerdigbehandlet && (
        <div className="space-y-2">
          <GodkjennOgBookPanel
            endpoint={`${API_BASE_URL}/akutt/${id}/godkjenn-book`}
            initialKategori={akutt.kategori}
            initialTjenesteSlug={akutt.tjenesteSlug}
            initialTjenester={akutt.tjenester}
            initialButikkSlug={akutt.onsketButikkSlug}
            onBooked={(data) => navigate(`/booking/${data.bookingId}`)}
          />
          <div className="flex justify-end">
            <button
              onClick={handleAvvis}
              disabled={avvising}
              className="text-xs px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              {avvising ? "…" : "Avvis forespørsel"}
            </button>
          </div>
        </div>
      )}

      {akutt.godkjent && akutt.bookingId && (
        <button
          onClick={() => navigate(`/booking/${akutt.bookingId}`)}
          className="w-full text-center px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg"
        >
          Åpne booking →
        </button>
      )}
    </div>
  );
}
